import React, { useState, useEffect } from 'react';
import { createAccessToken, getAccessStats, CreateAccessTokenRequest, CreateAccessTokenResponse } from '../services/api';
import { Shield, Clock, CheckCircle, BarChart3, Upload, Download, ExternalLink, Link2 } from 'lucide-react';
import { toast } from 'react-toastify';

const SecureAdminPage: React.FC = () => {
  const [ttlMinutes, setTtlMinutes] = useState(10);
  const [permissions, setPermissions] = useState<string[]>(['upload', 'download']);
  const [isCreating, setIsCreating] = useState(false);
  const [createdToken, setCreatedToken] = useState<CreateAccessTokenResponse | null>(null);
  const [stats, setStats] = useState<any>(null);
  const [isUrlButtonHovered, setIsUrlButtonHovered] = useState(false);

  // Load stats on component mount
  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const statsData = await getAccessStats();
      if (statsData && statsData.stats) {
        setStats(statsData.stats);
      }
    } catch (error: any) {
      console.error('Failed to load stats:', error);
      // Don't show error toast for stats - it's non-critical
      // Just set empty stats
      setStats({
        totalTokens: 0,
        activeTokens: 0,
        expiredTokens: 0,
        totalAccesses: 0
      });
    }
  };

  const handlePermissionChange = (permission: string, checked: boolean) => {
    if (checked) {
      setPermissions(prev => [...prev, permission]);
    } else {
      setPermissions(prev => prev.filter(p => p !== permission));
    }
  };

  const handleCreateToken = async () => {
    setIsCreating(true);
    setCreatedToken(null); // Clear previous result

    try {
      const request: CreateAccessTokenRequest = {
        ttlMinutes,
        permissions
      };

      console.log('🔄 Creating access token...', { ttlMinutes, permissions });
      const result = await createAccessToken(request);
      
      if (result.success) {
        setCreatedToken(result);
        toast.success('Access token created successfully!');
        // Refresh stats in background (don't wait for it)
        loadStats().catch(err => console.warn('Failed to refresh stats:', err));
      } else {
        throw new Error(result.message || 'Failed to create token');
      }
    } catch (error: any) {
      console.error('❌ Token creation failed:', error);
      const errorMessage = error.message || 'Failed to create access token';
      toast.error(errorMessage);
      // Show more helpful message for timeout
      if (errorMessage.includes('timed out') || errorMessage.includes('timeout')) {
        toast.info('The server may be starting up. Please wait a moment and try again.');
      }
    } finally {
      setIsCreating(false);
    }
  };

  // Generate unified frontend URL with token (single URL for all features)
  const generateFrontendUrl = (token: string) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/?token=${token}`;
  };

  // Handle button click: open URL in new tab
  const handleAccessUrlClick = (url: string) => {
    // Open URL in new tab
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-6">
        <h1 className="text-3xl font-bold text-gold mb-3 flex items-center justify-center gap-3">
          <Shield size={32} />
          Temporary Access Control Page
        </h1>
        <p className="text-lg text-gray-300">
          Generate temporary access URLs for DNA Storage System
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Create Token Section */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-xl font-bold text-gold flex items-center gap-2">
              <Shield size={24} />
              Generate Client Access URLs
            </h2>
          </div>

          <div className="space-y-4">
            {/* TTL Setting */}
            <div>
              <label className="block text-sm font-semibold text-gold mb-2">
                Access Duration (Minutes)
              </label>
              {/* Quick preset buttons */}
              <div className="flex gap-2 mb-2 flex-wrap">
                {[5, 10, 15, 20].map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => setTtlMinutes(preset)}
                    disabled={isCreating}
                    className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                      ttlMinutes === preset
                        ? 'bg-gold text-black border-2 border-gold'
                        : 'bg-gray-700 text-gray-300 border border-gray-600 hover:bg-gray-600 hover:text-white'
                    }`}
                  >
                    {preset} min
                  </button>
                ))}
              </div>
              <input
                type="number"
                value={ttlMinutes}
                onChange={(e) => setTtlMinutes(parseInt(e.target.value) || 10)}
                min="1"
                max="60"
                className="input"
                disabled={isCreating}
              />
              <p className="text-xs text-gray-400 mt-1">
                Client will have access for this many minutes after first use (1-60 minutes)
              </p>
            </div>

            {/* Permissions */}
            <div>
              <label className="block text-sm font-semibold text-gold mb-2">
                Client Permissions
              </label>
              <div className="space-y-2">
                {[
                  { key: 'upload', label: 'Upload Files', description: 'Encode files to DNA', icon: Upload },
                  { key: 'download', label: 'Download Files', description: 'Decode DNA to files', icon: Download }
                ].map((permission) => (
                  <label key={permission.key} className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-700">
                    <input
                      type="checkbox"
                      checked={permissions.includes(permission.key)}
                      onChange={(e) => handlePermissionChange(permission.key, e.target.checked)}
                      className="w-4 h-4 text-gold bg-gray-700 border-gray-600 rounded focus:ring-gold focus:ring-2"
                      disabled={isCreating}
                    />
                    <permission.icon size={20} className="text-gold" />
                    <div>
                      <div className="text-white font-medium">{permission.label}</div>
                      <div className="text-gray-400 text-sm">{permission.description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Create Button */}
            <button
              onClick={handleCreateToken}
              disabled={isCreating || permissions.length === 0}
              className="btn btn-primary w-full flex items-center justify-center gap-2"
            >
              {isCreating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-black border-t-transparent"></div>
                  Generating...
                </>
              ) : (
                <>
                  <Shield size={20} />
                  Generate Client URLs
                </>
              )}
            </button>
          </div>

          {/* Generated URLs Display */}
          {createdToken && (
            <div className="mt-6 pt-6 border-t border-gray-700">
              <div className="bg-green-900 bg-opacity-20 border border-green-500 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle size={20} className="text-green-400" />
                  <h3 className="text-green-400 font-semibold margin-top-5 margin-bottom-5">Client Access URLs Generated!</h3>
                </div>
                
                <div className="space-y-4">
                  {/* Unified System URL */}
                  <div>
                    <label className="text-sm text-gray-300 flex items-center gap-2 mb-3 mt-5">
                      <Shield size={16} />
                      System Access URL (Upload & Download):
                    </label>
                    <button
                      onClick={() => handleAccessUrlClick(generateFrontendUrl(createdToken.token))}
                      onMouseEnter={() => setIsUrlButtonHovered(true)}
                      onMouseLeave={() => setIsUrlButtonHovered(false)}
                      className="w-full group relative overflow-hidden transition-all duration-300 ease-in-out"
                      style={{
                        padding: '18px 28px',
                        border: '2px solid #31B9C7',
                        backgroundColor: isUrlButtonHovered ? '#31B9C7' : 'transparent',
                        color: isUrlButtonHovered ? '#000000' : '#31B9C7',
                        fontSize: '15px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        marginTop: '12px',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '12px',
                        marginBottom: '12px',
                        transform: isUrlButtonHovered ? 'translateY(-3px)' : 'translateY(0)',
                        boxShadow: isUrlButtonHovered ? '0 8px 24px rgba(49, 185, 199, 0.4)' : 'none',
                      }}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <Link2 
                          size={20} 
                          className="flex-shrink-0 transition-transform duration-300"
                          style={{ transform: isUrlButtonHovered ? 'scale(1.1)' : 'scale(1)' }}
                        />
                        <span className="text-sm text-gray-300">Click to open URL in new tab</span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <ExternalLink 
                          size={18} 
                          className="transition-opacity duration-300"
                          style={{ opacity: isUrlButtonHovered ? 1 : 0.7 }}
                        />
                      </div>
                    </button>
                    <p className="text-xs text-gray-400 mt-2">
                      Click to open URL in new tab. This single URL provides access to both Upload (Engrave) and Download (Decode) features. Use the navigation buttons in the header to switch between features.
                    </p>
                  </div>

                  <div className="flex items-center gap-4 text-sm text-gray-300 pt-2 border-t border-gray-600">
                    <span className="flex items-center gap-1">
                      <Clock size={14} />
                      Valid for: {Math.floor(createdToken.expiresIn / 60)} minutes
                    </span>
                    <span className="flex items-center gap-1">
                      <BarChart3 size={14} />
                      Token ID: {createdToken.tokenId}
                    </span>
                  </div>

                  <div className="mt-4 p-3 bg-blue-900 bg-opacity-20 border border-blue-500 rounded">
                    <div className="flex items-center gap-2 mb-2">
                      {/* <AlertCircle size={16} className="text-blue-400" />
                      <span className="text-blue-400 font-semibold text-sm">Instructions</span> */}
                    </div>
                    {/* <p className="text-blue-300 text-sm">
                      Send this URL to your client. They can access both upload and download features for <strong>{Math.floor(createdToken.expiresIn / 60)} minutes</strong> after first use. </p> */}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Statistics Section */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-bold text-gold flex items-center gap-2">
              <BarChart3 size={20} />
              Access Statistics
            </h3>
          </div>
          
          {stats ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-gray-800 rounded-lg">
                  <div className="text-2xl font-bold text-gold">{stats.totalTokens}</div>
                  <div className="text-sm text-gray-400">Total Tokens</div>
                </div>
                <div className="text-center p-4 bg-gray-800 rounded-lg">
                  <div className="text-2xl font-bold text-green-400">{stats.activeTokens}</div>
                  <div className="text-sm text-gray-400">Active Tokens</div>
                </div>
                <div className="text-center p-4 bg-gray-800 rounded-lg">
                  <div className="text-2xl font-bold text-red-400">{stats.expiredTokens}</div>
                  <div className="text-sm text-gray-400">Expired Tokens</div>
                </div>
                <div className="text-center p-4 bg-gray-800 rounded-lg">
                  <div className="text-2xl font-bold text-blue-400">{stats.totalAccesses}</div>
                  <div className="text-sm text-gray-400">Total Accesses</div>
                </div>
              </div>

              <div className="bg-gray-800 rounded-lg p-4">
                <h4 className="text-gold font-semibold mb-3">System Status</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-300">Access Control:</span>
                    <span className="text-green-400">Active</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">Token Cleanup:</span>
                    <span className="text-green-400">Automatic</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">Security Level:</span>
                    <span className="text-blue-400">High</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-gold border-t-transparent mx-auto mb-4"></div>
              <p className="text-gray-400">Loading statistics...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SecureAdminPage;
